import React from 'react';
import styled, { keyframes } from 'styled-components';
import moment from 'moment';

export const STATUS_PILL: Record<string, { bg: string; fg: string; dot: string; label: string }> = {
  RUNNING: { bg: '#BFDBFE', fg: '#1D4ED8', dot: '#3B82F6', label: 'Running' },
  QUEUED: { bg: '#DBEAFE', fg: '#2563EB', dot: '#60A5FA', label: 'Queued' },
  PENDING: { bg: '#DBEAFE', fg: '#2563EB', dot: '#60A5FA', label: 'Starting' },
  SUCCEEDED: { bg: '#BBF7D0', fg: '#15803D', dot: '#22C55E', label: 'Succeeded' },
  FAILED: { bg: '#FECACA', fg: '#B91C1C', dot: '#EF4444', label: 'Failed' },
  CANCELLED: { bg: '#E2E8F0', fg: '#334155', dot: '#94A3B8', label: 'Cancelled' },
};

export type StepState = 'done' | 'running' | 'queued' | 'waiting' | 'failed' | 'cancelled';

export const STEP_LABEL: Record<StepState, string> = {
  done: 'Done',
  running: 'Running',
  queued: 'Queued',
  waiting: 'Waiting',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STEP_COLORS: Record<StepState, { bg: string; fg: string; dot: string }> = {
  done: { bg: '#BBF7D0', fg: '#15803D', dot: '#22C55E' },
  running: { bg: '#BFDBFE', fg: '#1D4ED8', dot: '#3B82F6' },
  queued: { bg: '#DBEAFE', fg: '#2563EB', dot: '#60A5FA' },
  waiting: { bg: '#E2E8F0', fg: '#334155', dot: '#94A3B8' },
  failed: { bg: '#FECACA', fg: '#B91C1C', dot: '#EF4444' },
  cancelled: { bg: '#E2E8F0', fg: '#334155', dot: '#94A3B8' },
};

const REGIONS: Record<string, string> = { plc: 'Poland', swe: 'Sweden', esc: 'Spain', weu: 'West Europe' };

export const whereOf = (pool?: string | null) => {
  if (!pool) return '';
  const region = Object.keys(REGIONS).find((k) => pool.includes(`-${k}-`));
  const machine = pool.includes('cpu') ? 'CPU' : pool.includes('a10') ? 'A10' : '';
  return [region && REGIONS[region], machine].filter(Boolean).join(' · ');
};

export const formatTime = (ms?: number) => (ms ? moment(ms).tz('Europe/Amsterdam').format('D MMM HH:mm') : '—');

export const formatElapsed = (from?: number, to?: number) => {
  if (!from) return '—';
  const seconds = Math.max(0, Math.round(((to || Date.now()) - from) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} m ${String(seconds % 60).padStart(2, '0')} s`;
};

export const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

export const SummaryTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

export const SummaryTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const SummaryTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  h2 {
    margin: 0;
  }
`;

export const SessionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 8px;
  border-radius: 4px;
`;

const StatusPillBox = styled.span<{ bg: string; fg: string; dot: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
  i {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: ${({ dot }) => dot};
  }
`;

export const StatusPill: React.FunctionComponent<{ status: string; label?: string }> = ({ status, label }) => {
  const pill = STATUS_PILL[status] || STATUS_PILL.PENDING;
  return (
    <StatusPillBox bg={pill.bg} fg={pill.fg} dot={pill.dot}>
      <i />
      {label || pill.label}
    </StatusPillBox>
  );
};

export const SummaryFacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 18px;
  color: #64748b;
  font-size: 14px;
`;

export const SummaryActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const SummaryDetails = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px 24px;
  margin: 0;
  font-variant-numeric: tabular-nums;
  dt {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 2px;
  }
  dd {
    margin: 0;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
`;

export const Mono = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
`;

const sweep = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
`;

export const FlowCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
`;

export const FlowHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  h2 {
    margin: 0;
  }
`;

export const FlowOverall = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  div:first-child {
    display: flex;
    justify-content: space-between;
  }
  strong {
    color: #1d4ed8;
    font-size: 16px;
  }
`;

export const Track = styled.div<{ thin?: boolean }>`
  height: ${({ thin }) => (thin ? '5px' : '8px')};
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

export const Fill = styled.i<{ percent: number; state: StepState }>`
  display: block;
  height: 100%;
  width: ${({ percent }) => percent}%;
  border-radius: 999px;
  background-color: ${({ state }) => (state === 'done' ? '#22C55E' : state === 'failed' ? '#EF4444' : '#3B82F6')};
  ${({ state }) =>
    state === 'running' &&
    `background-image: linear-gradient(90deg, transparent 0 40%, rgba(255,255,255,.35) 50%, transparent 60% 100%);
     background-size: 200% 100%;`}
  @media (prefers-reduced-motion: no-preference) {
    animation: ${({ state }) => (state === 'running' ? sweep : 'none')} 2.4s linear infinite;
  }
`;

export const StepNode = styled.div<{ state: StepState }>`
  border: 1px solid
    ${({ state }) => (state === 'running' ? '#3B82F6' : state === 'done' ? '#BBF7D0' : state === 'failed' ? '#FECACA' : '#E2E8F0')};
  background: ${({ state }) => (state === 'running' ? '#F4F7FF' : '#FFFFFF')};
  border-radius: 6px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const StepNodeTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  a {
    color: inherit;
  }
`;

export const StepMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

export const StepError = styled.div`
  font-size: 12px;
  color: #b91c1c;
  overflow-wrap: anywhere;
`;

const StepProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  > div {
    flex: 1 1 auto;
  }
  b {
    font-size: 12px;
    min-width: 34px;
    text-align: right;
    color: #1d4ed8;
    font-variant-numeric: tabular-nums;
  }
`;

export const StepProgress: React.FunctionComponent<{ percent: number; state: StepState }> = ({ percent, state }) => (
  <StepProgressRow>
    <Track thin>
      <Fill percent={percent} state={state} />
    </Track>
    <b>{percent}%</b>
  </StepProgressRow>
);

const StepPillBox = styled.span<{ state: StepState }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${({ state }) => STEP_COLORS[state].bg};
  color: ${({ state }) => STEP_COLORS[state].fg};
  i {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: ${({ state }) => STEP_COLORS[state].dot};
  }
`;

export const StepPill: React.FunctionComponent<{ state: StepState }> = ({ state }) => (
  <StepPillBox state={state}>
    <i />
    {STEP_LABEL[state]}
  </StepPillBox>
);

export const FlowLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  color: #1e293b;
  font-weight: 500;
  text-decoration: none;
`;
